import React, { useState, useEffect } from 'react';
import WaterDropRoundedIcon from '@mui/icons-material/WaterDropRounded';
import ShowerRoundedIcon from '@mui/icons-material/ShowerRounded';
import EnergySavingsLeafRoundedIcon from '@mui/icons-material/EnergySavingsLeafRounded';
import BathtubRoundedIcon from '@mui/icons-material/BathtubRounded';
import LocalLaundryServiceRoundedIcon from '@mui/icons-material/LocalLaundryServiceRounded';
import RecommendationCard from '../../components/RecommendationCard/RecommendationCard';
import { calculateDailyLimit, getConsumptionStatus } from '../../utils/waterConsumption';
import { useUserConfig } from '../../context/UserConfigContext';
import { useUnit } from '../../context/UnitContext';
import './Recommendations.css';

export default function Recommendations() {
  const { householdMembers, dailyGoal } = useUserConfig();
  const { formatValueWithUnit } = useUnit();
  const [userMetrics, setUserMetrics] = useState(null);
  const [recommendations, setRecommendations] = useState([]);

  useEffect(() => {
    // Aquí deberías obtener las métricas reales del usuario desde tu backend
    // Por ahora usaremos datos de ejemplo
    const mockUserMetrics = {
      dailyConsumption: 250, // litros por día
      showerTime: 12, // minutos promedio
      washingMachineUses: 4, // veces por semana
      bathFrequency: 2, // veces por semana
      leakDetection: false,
      gardenArea: 50, // metros cuadrados
      householdMembers: householdMembers, // número de personas en el hogar
    };

    setUserMetrics(mockUserMetrics);
    generateRecommendations(mockUserMetrics);
    // eslint-disable-next-line
  }, [householdMembers, dailyGoal]);

  const generateRecommendations = (metrics) => {
    const personalizedRecommendations = [];
    const dailyLimit = dailyGoal !== '' ? dailyGoal : calculateDailyLimit(metrics.householdMembers);
    const consumptionStatus = getConsumptionStatus(metrics.dailyConsumption, metrics.householdMembers, dailyGoal !== '' ? dailyGoal : undefined);

    // Recomendación basada en el consumo total
    if (consumptionStatus.status !== 'success') {
      personalizedRecommendations.push({
        title: "Consumo de agua elevado",
        description: `El consumo actual de ${formatValueWithUnit(metrics.dailyConsumption)} está por encima del límite recomendado de ${formatValueWithUnit(dailyLimit)} para ${metrics.householdMembers} personas.`,
        icon: <WaterDropRoundedIcon />,
        impact: `${formatValueWithUnit(metrics.dailyConsumption - dailyLimit)}/día`,
        category: "Consumo"
      });
    }

    // Recomendación basada en tiempo de ducha
    if (metrics.showerTime > 8) {
      const showerSavings = Math.round(metrics.showerTime * 10 * metrics.householdMembers);
      personalizedRecommendations.push({
        title: "Optimiza tu tiempo de ducha",
        description: `Con ${metrics.householdMembers} personas en casa, reducir el tiempo de ducha de ${metrics.showerTime} a 5 minutos podría ahorrar hasta ${formatValueWithUnit(showerSavings)} por día.`,
        icon: <ShowerRoundedIcon />,
        impact: `${formatValueWithUnit(showerSavings)}/día`,
        category: "Ducha"
      });
    }

    // Recomendación para lavadora
    if (metrics.washingMachineUses > 3) {
      const weeklySavings = 40 * (metrics.washingMachineUses - 3);
      personalizedRecommendations.push({
        title: "Optimiza el uso de la lavadora",
        description: `Con ${metrics.householdMembers} personas, intenta consolidar la ropa y reducir el uso de la lavadora a 3 veces por semana.`,
        icon: <LocalLaundryServiceRoundedIcon />,
        impact: `${formatValueWithUnit(weeklySavings)}/semana`,
        category: "Lavadora"
      });
    }

    // Recomendación para baño
    if (metrics.bathFrequency > 1) {
      const bathSavings = 100 * metrics.householdMembers;
      personalizedRecommendations.push({
        title: "Alterna baño y ducha",
        description: `Con ${metrics.householdMembers} personas en casa, alternar entre baño y ducha podría ahorrar hasta ${formatValueWithUnit(bathSavings)} por semana.`,
        icon: <BathtubRoundedIcon />,
        impact: `${formatValueWithUnit(bathSavings)}/semana`,
        category: "Baño"
      });
    }

    // Recomendación para jardín
    if (metrics.gardenArea > 0) {
      personalizedRecommendations.push({
        title: "Riego eficiente",
        description: `Para tu jardín de ${metrics.gardenArea}m², considera instalar un sistema de riego por goteo y regar en horarios de menor evaporación.`,
        icon: <EnergySavingsLeafRoundedIcon />,
        impact: "30% menos agua",
        category: "Jardín"
      });
    }

    // Recomendación general de mantenimiento
    if (!metrics.leakDetection) {
      personalizedRecommendations.push({
        title: "Revisión de fugas",
        description: "No se ha detectado una revisión reciente de fugas. Una gotera puede desperdiciar hasta 200 litros diarios.",
        icon: <WaterDropRoundedIcon />,
        impact: `${formatValueWithUnit(200)}/día`,
        category: "Mantenimiento"
      });
    }

    setRecommendations(personalizedRecommendations);
  };

  return (
    <div className="recommendations-container">
      <h1>Recomendaciones Personalizadas</h1>
      {userMetrics && (
        <div className="user-metrics-summary">
          <h2>Tu Consumo Actual</h2>
          <p>Consumo diario promedio: {formatValueWithUnit(userMetrics.dailyConsumption)}</p>
          <p>Límite diario recomendado: {formatValueWithUnit(dailyGoal !== '' ? dailyGoal : calculateDailyLimit(householdMembers))}</p>
          <p>Personas en el hogar: {householdMembers}</p>
        </div>
      )}
      <p className="recommendations-intro">
        Basado en tus hábitos de consumo y el número de personas en tu hogar, te presentamos estas recomendaciones personalizadas para optimizar el uso del agua.
      </p>
      <div className="recommendations-grid">
        {recommendations.map((rec, index) => (
          <RecommendationCard
            key={index}
            title={rec.title}
            description={rec.description}
            icon={rec.icon}
            impact={rec.impact}
            category={rec.category}
          />
        ))}
      </div>
    </div>
  );
}
